/**
 * Condiciones de pago por proveedor. Un proveedor ofrece varias (la predeterminada más las de
 * backend/src/compras/condiciones-proveedor.ts); la OC puede dejar la condición "a definir" y la factura la decide.
 * faltaCondicionFactura es el espejo de la regla de backend/src/finanzas/finanzas.service.ts (crearFactura).
 */

export interface CondicionPagoRef { id: number; nombre: string; tipo?: string }

export const ETIQUETA_A_DEFINIR = 'A definir';

/** Ofrecidas por el proveedor (en su orden, solo las que están en el catálogo) y el resto del catálogo. */
export function separarCondiciones<C extends CondicionPagoRef>(catalogo: C[], ofrecidas: CondicionPagoRef[] | undefined) {
  const porId = new Map(catalogo.map(c => [c.id, c]));
  const elegidas = (ofrecidas ?? []).map(o => porId.get(o.id)).filter((c): c is C => c != null);
  const ids = new Set(elegidas.map(c => c.id));
  return { ofrecidas: elegidas, otras: catalogo.filter(c => !ids.has(c.id)) };
}

interface CondicionOC {
  condicionPago?: string | null;
  condicionPagoId?: number | null;
  condicionPagoFechaVencimiento?: string | null;
}

/** Condición de una OC para mostrar: su nombre; "Otro" si solo tiene términos propios; si no, "A definir". */
export function etiquetaCondicionOC(oc: CondicionOC): string {
  const nombre = oc.condicionPago?.trim();
  if (nombre) return nombre;
  return oc.condicionPagoFechaVencimiento ? 'Otro' : ETIQUETA_A_DEFINIR;
}

/**
 * Si a una factura común le falta la condición de pago: sin vencimiento manual ni condición elegida, y la OC no
 * resuelve ninguna (ni condición por id o por nombre, ni fecha ad-hoc). 'oc' = la OC la dejó a definir;
 * 'sin-oc' = la factura no tiene OC. null = no falta (o todavía no se sabe: la OC no se cargó, o tiene un nombre que
 * no está en el catálogo activo, que puede ser una condición desactivada; decide el backend).
 */
export function faltaCondicionFactura(p: {
  facturaComun: boolean; fechaVencimiento: string; condicionPago: string;
  tieneOC: boolean; oc: CondicionOC | null;
}): 'oc' | 'sin-oc' | null {
  if (!p.facturaComun || p.fechaVencimiento || p.condicionPago) return null;
  if (!p.tieneOC) return 'sin-oc';
  if (!p.oc) return null;
  const { condicionPagoId, condicionPago, condicionPagoFechaVencimiento } = p.oc;
  const resuelve = condicionPagoId != null || !!condicionPagoFechaVencimiento || !!condicionPago;
  return resuelve ? null : 'oc';
}

export const MENSAJE_FALTA_CONDICION: Record<'oc' | 'sin-oc', string> = {
  oc: 'Elegí la condición de pago de la factura o cargá la fecha de vencimiento: la OC tiene la condición a definir.',
  'sin-oc': 'Elegí la condición de pago de la factura o cargá la fecha de vencimiento.',
};
